import { isValidElement, JSX, ReactNode } from 'react';
import { Divider, ModalProps as HeroUIModalProps } from '@heroui/react';
import {
  Modal as HeroUIModal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@heroui/react';

import useMergeClassNames from '~/hooks/useMergeClassNames';

type MaxWidth =
  /** 20rem (320px) */
  | 'xs'
  /** 24rem (384px) */
  | 'sm'
  /** 28rem (448px) */
  | 'md'
  /** 32rem (512px) */
  | 'lg'
  /** 36rem (576px) */
  | 'xl'
  /** 42rem (672px) */
  | '2xl'
  /** 48rem (768px) */
  | '3xl'
  /** 56rem (896px) */
  | '4xl'
  /** 64rem (1024px) */
  | '5xl';

export interface ModalProps extends HeroUIModalProps {
  /**
   * The maximum width of the modal.
   * @default '5xl'
   */
  maxWidth?: MaxWidth;
  /**
   * Whether the modal is dismissable or not.
   * If true, the modal can't be dismissed by clicking outside of it or pressing the escape key.
   * For forms, use the isDirty prop to control the dismiss behavior.
   *
   * @default false
   */
  preventClosing?: boolean;
  /**
   * Optional header content. Only used when children don't contain ModalContent.
   * For simple modals, provide header as a prop.
   * For complex modals, use composition with ModalContent/ModalHeader/ModalBody/ModalFooter as children.
   */
  header?: ReactNode;
  /**
   * Optional footer content. Only used when children don't contain ModalContent.
   */
  footer?: ReactNode;
}

export function Modal(props: ModalProps): JSX.Element {
  const {
    children,
    classNames: customClassNames,
    footer,
    header,
    maxWidth = '5xl',
    preventClosing = false,
    ...rest
  } = props;

  const maxWidthClass = {
    xs: 'max-w-xs',
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
  } satisfies Record<MaxWidth, string>;

  const classNames = useMergeClassNames(
    {
      base: [
        'w-full',
        maxWidthClass[maxWidth],
        'max-h-[85vh]',
        'rounded-xl',
        'border-1',
        'border-default',
        'bg-surface',
      ],
      header: 'p-4',
      body: 'p-4 gap-4',
      footer: 'p-4',
    },
    customClassNames,
  );

  let content: ReactNode;

  // Check if children contains ModalContent (composition pattern)
  if (isValidElement(children) && children.type === ModalContent) {
    content = children;
  } else {
    content = (
      <>
        <ModalContent>
          {header && (
            <>
              <ModalHeader>{header}</ModalHeader>
              <Divider />
            </>
          )}
          <ModalBody>{children}</ModalBody>
          {footer && <ModalFooter>{footer}</ModalFooter>}
        </ModalContent>
      </>
    );
  }

  return (
    <HeroUIModal
      backdrop="blur"
      classNames={classNames}
      data-test-id="Modal"
      placement="center"
      scrollBehavior="inside"
      size="5xl"
      {...rest}
      isDismissable={!preventClosing}
      isKeyboardDismissDisabled={preventClosing}
    >
      {content}
    </HeroUIModal>
  );
}

export { ModalBody, ModalContent, ModalFooter, ModalHeader } from '@heroui/react';
